import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Pencil, Trash2, X, Tag, ChevronUp, ChevronDown } from "lucide-react";
import { api, authHeaders } from "../lib/api";

export default function CategoryManager({ onChange }) {
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/categories");
            setCats(data);
            onChange?.(data);
        } catch {
            toast.error("Could not load categories");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const add = async (e) => {
        e.preventDefault();
        const name = newName.trim();
        if (!name) return;
        try {
            const order = cats.length + 1;
            await api.post(
                "/categories",
                { name, order },
                { headers: authHeaders() }
            );
            setNewName("");
            await load();
            toast.success(`Added "${name}"`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Add failed");
        }
    };

    const startEdit = (c) => {
        setEditingId(c.id);
        setEditingName(c.name);
    };

    const saveEdit = async () => {
        const name = editingName.trim();
        if (!name) return;
        try {
            await api.patch(
                `/categories/${editingId}`,
                { name },
                { headers: authHeaders() }
            );
            setEditingId(null);
            setEditingName("");
            await load();
            toast.success("Updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete category "${c.name}"?`)) return;
        try {
            await api.delete(`/categories/${c.id}`, { headers: authHeaders() });
            await load();
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const move = async (index, direction) => {
        const j = index + direction;
        if (j < 0 || j >= cats.length) return;
        const a = cats[index];
        const b = cats[j];
        // Optimistic reorder in UI
        const reordered = [...cats];
        reordered[index] = b;
        reordered[j] = a;
        setCats(reordered);
        try {
            await Promise.all([
                api.patch(
                    `/categories/${a.id}`,
                    { order: j + 1 },
                    { headers: authHeaders() }
                ),
                api.patch(
                    `/categories/${b.id}`,
                    { order: index + 1 },
                    { headers: authHeaders() }
                ),
            ]);
            await load();
        } catch {
            toast.error("Reorder failed");
            await load();
        }
    };

    const inputCls =
        "flex-1 bg-black border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] outline-none";

    return (
        <section
            data-testid="category-manager"
            className="bg-[#121212] border border-white/10 rounded-2xl p-5 space-y-4"
        >
            <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
                <Tag size={18} className="kw-orange" /> Categories
            </h2>

            <form onSubmit={add} className="flex gap-2">
                <input
                    data-testid="category-new-input"
                    placeholder="New category name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className={inputCls}
                />
                <button
                    data-testid="category-add-btn"
                    type="submit"
                    className="px-4 py-2 kw-orange-bg text-black font-bold rounded-lg uppercase tracking-wider text-xs flex items-center gap-1"
                >
                    <Plus size={14} /> Add
                </button>
            </form>

            {loading && !cats.length ? (
                <div className="text-white/40 text-sm">Loading…</div>
            ) : cats.length === 0 ? (
                <div className="text-white/40 text-sm py-4 text-center border border-dashed border-white/10 rounded-xl">
                    No categories yet — add your first above.
                </div>
            ) : (
                <ul
                    data-testid="category-list"
                    className="space-y-2"
                >
                    {cats.map((c, i) => (
                        <li
                            key={c.id}
                            data-testid={`category-item-${c.id}`}
                            className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl p-2 pl-3"
                        >
                            {editingId === c.id ? (
                                <>
                                    <input
                                        data-testid={`category-edit-input-${c.id}`}
                                        value={editingName}
                                        autoFocus
                                        onChange={(e) =>
                                            setEditingName(e.target.value)
                                        }
                                        onKeyDown={(e) =>
                                            e.key === "Enter" && saveEdit()
                                        }
                                        className={inputCls}
                                    />
                                    <button
                                        data-testid={`category-save-${c.id}`}
                                        onClick={saveEdit}
                                        className="p-2 rounded-lg text-[#FF5A00] hover:bg-white/5"
                                        aria-label="Save"
                                    >
                                        <Check size={16} />
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingId(null);
                                            setEditingName("");
                                        }}
                                        className="p-2 rounded-lg text-white/50 hover:text-white"
                                        aria-label="Cancel"
                                    >
                                        <X size={16} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="flex flex-col -space-y-1">
                                        <button
                                            data-testid={`category-up-${c.id}`}
                                            onClick={() => move(i, -1)}
                                            disabled={i === 0}
                                            aria-label="Move up"
                                            className="p-1 rounded text-white/40 hover:text-[#FF5A00] disabled:opacity-20 disabled:hover:text-white/40"
                                        >
                                            <ChevronUp size={14} />
                                        </button>
                                        <button
                                            data-testid={`category-down-${c.id}`}
                                            onClick={() => move(i, 1)}
                                            disabled={i === cats.length - 1}
                                            aria-label="Move down"
                                            className="p-1 rounded text-white/40 hover:text-[#FF5A00] disabled:opacity-20 disabled:hover:text-white/40"
                                        >
                                            <ChevronDown size={14} />
                                        </button>
                                    </div>
                                    <span className="flex-1 text-sm truncate">
                                        {c.name}
                                    </span>
                                    <button
                                        data-testid={`category-edit-${c.id}`}
                                        onClick={() => startEdit(c)}
                                        className="p-2 rounded-lg text-white/50 hover:text-[#FF5A00]"
                                        aria-label="Edit"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        data-testid={`category-delete-${c.id}`}
                                        onClick={() => remove(c)}
                                        className="p-2 rounded-lg text-white/50 hover:text-[#FF5A00]"
                                        aria-label="Delete"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
